import React, { useState, useEffect, useCallback, useMemo, memo } from "react"
import { 
  Input, 
  Button, 
  Card, 
  message, 
  Space, 
  Typography, 
  Tag,
  Modal,
  Form,
  Select,
  Row,
  Col,
  Empty,
  Skeleton,
  Badge,
  Tooltip,
  Avatar,
  Divider
} from "antd"
import { 
  SearchOutlined, 
  EditOutlined, 
  EyeOutlined,
  ReloadOutlined,
  ScheduleFilled,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  GlobalOutlined,
  BookOutlined,
  TeamOutlined,
  TrophyOutlined,
  IdcardOutlined,
  PlusOutlined
} from "@ant-design/icons"
import axios from "axios"
import "./IC3SchoolDashboard.css"
const constants = require("../../constants")

const { Title, Text } = Typography
const { Option } = Select

// Memoized School Card Component for better performance
const SchoolCard = memo(({ school, onView, onEdit }) => {
  const getColorForStats = useCallback((num) => {
    if (num >= 100) return "#52c41a"
    if (num >= 50) return "#1890ff"
    if (num >= 10) return "#faad14"
    return "#8c8c8c"
  }, [])

  return (
    <Badge.Ribbon 
      text={school.schoolId || "No ID"} 
      color={school.schoolId ? "blue" : "gray"}
    >
      <Card
        hoverable
        onClick={() => onView(school)}
        className="school-card"
        styles={{ body: { padding: "16px" } }}
        actions={[
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={(e) => {
                e.stopPropagation()
                onView(school)
              }}
              size="small"
            >
              <span className="action-text">View</span>
            </Button>
          </Tooltip>,
          <Tooltip title="Edit School">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={(e) => onEdit(school, e)}
              style={{ color: "#1890ff" }}
              size="small"
            >
              <span className="action-text">Edit</span>
            </Button>
          </Tooltip>
        ]}
      >
        <div className="school-card-header">
          <Avatar 
            size={48} 
            icon={<ScheduleFilled />} 
            className="school-avatar"
          />
          <Title level={5} className="school-title">
            {school.schoolName}
          </Title>
        </div>

        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <div className="info-item">
            <UserOutlined className="info-icon" />
            <Text type="secondary" className="info-text">{school.poc?.name || "No POC"}</Text>
          </div>
          
          <div className="info-item">
            <PhoneOutlined className="info-icon" />
            <Text type="secondary" className="info-text">{school.poc?.phone || "No Phone"}</Text>
          </div>

          {school.poc?.email && (
            <div className="info-item">
              <MailOutlined className="info-icon" />
              <Text type="secondary" ellipsis className="info-text">{school.poc.email}</Text>
            </div>
          )}

          <Divider className="card-divider" />

          <div className="programs-section">
            <Text strong className="section-title">
              <BookOutlined /> Programs
            </Text>
            <div className="tags-container">
              {school.interestedPrograms && school.interestedPrograms.length > 0 ? (
                school.interestedPrograms.slice(0, 2).map((program, index) => (
                  <Tag 
                    key={index} 
                    color="blue" 
                    className="program-tag"
                  >
                    {program}
                  </Tag>
                ))
              ) : (
                <Tag color="default">No Programs</Tag>
              )}
              {school.interestedPrograms && school.interestedPrograms.length > 2 && (
                <Tag>+{school.interestedPrograms.length - 2}</Tag>
              )}
            </div>
          </div>

          <Row gutter={8}>
            <Col span={12}>
              <div className="stat-box">
                <TeamOutlined style={{ fontSize: "18px", color: getColorForStats(school.numberOfPaidUsers) }} />
                <div>
                  <Text strong style={{ fontSize: "18px", color: getColorForStats(school.numberOfPaidUsers) }}>
                    {school.numberOfPaidUsers || 0}
                  </Text>
                </div>
                <Text type="secondary" className="stat-label">Paid Users</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="stat-box">
                <TrophyOutlined style={{ fontSize: "18px", color: getColorForStats(school.numberOfLeads) }} />
                <div>
                  <Text strong style={{ fontSize: "18px", color: getColorForStats(school.numberOfLeads) }}>
                    {school.numberOfLeads || 0}
                  </Text>
                </div>
                <Text type="secondary" className="stat-label">Leads</Text>
              </div>
            </Col>
          </Row>
        </Space>
      </Card>
    </Badge.Ribbon>
  )
})

const IC3SchoolDashboard = () => {
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedSchool, setSelectedSchool] = useState(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [form] = Form.useForm()
  const [displayCount, setDisplayCount] = useState(12) // Initial display count for virtual scrolling
  const [cities, setCities] = useState([])
  const [citiesLoading, setCitiesLoading] = useState(false)

  // Fetch all schools
  const fetchAllSchools = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${constants.NEW_DOMAIN}/api/v1/ic3school`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_JWT_TOKEN}`
        }
      })
      setSchools(response.data.data.schools || [])
    } catch (error) {
      message.error("Failed to fetch schools")
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch cities from API
  const fetchCities = async (searchText = "") => {
    setCitiesLoading(true)
    try {
      const response = await axios.get(`${constants.NEW_DOMAIN}/api/v1/misc/cities`, {
        params: { search: searchText }
      })
      setCities(response.data.data.cities || [])
    } catch (error) {
      console.error("Failed to fetch cities:", error)
    } finally {
      setCitiesLoading(false)
    }
  }

  // Optimized search with useMemo
  const filteredSchools = useMemo(() => {
    if (!searchTerm) return schools

    const searchLower = searchTerm.toLowerCase()
    return schools.filter(school => 
      school.schoolName?.toLowerCase().includes(searchLower) ||
      school.schoolId?.toLowerCase().includes(searchLower) ||
      school.poc?.name?.toLowerCase().includes(searchLower) ||
      school.poc?.email?.toLowerCase().includes(searchLower) ||
      school.poc?.phone?.toLowerCase().includes(searchLower)
    )
  }, [schools, searchTerm])

  // Debounced search handler
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value)
  }, [])

  // Show school details
  const showSchoolDetails = useCallback((school) => {
    setSelectedSchool(school)
    setIsEditMode(false)
    setIsCreateMode(false)
    form.setFieldsValue({
      schoolName: school.schoolName,
      schoolType: school.schoolType,
      place: school.place,
      "poc.name": school.poc?.name,
      "poc.email": school.poc?.email,
      "poc.phone": school.poc?.phone,
      "poc.designation": school.poc?.designation,
      "poc.secondaryPOC": school.poc?.secondaryPOC,
      "poc.secondaryPhone": school.poc?.secondaryPhone,
      interestedPrograms: school.interestedPrograms,
      numberOfPaidUsers: school.numberOfPaidUsers,
      numberOfLeads: school.numberOfLeads,
      websiteUrl: school.websiteUrl,
      schoolId: school.schoolId,
      notes: school.notes,
      timing: school.timing
    })
    setIsModalVisible(true)
  }, [form])

  // Enable create mode
  const enableCreateMode = useCallback(() => {
    setSelectedSchool(null)
    setIsEditMode(false)
    setIsCreateMode(true)
    form.resetFields()
    setIsModalVisible(true)
  }, [form])

  // Enable edit mode
  const enableEditMode = useCallback((school, e) => {
    e?.stopPropagation()
    setSelectedSchool(school)
    setIsEditMode(true)
    setIsCreateMode(false)
    form.setFieldsValue({
      schoolName: school.schoolName,
      schoolType: school.schoolType,
      place: school.place,
      "poc.name": school.poc?.name,
      "poc.email": school.poc?.email,
      "poc.phone": school.poc?.phone,
      "poc.designation": school.poc?.designation,
      "poc.secondaryPOC": school.poc?.secondaryPOC,
      "poc.secondaryPhone": school.poc?.secondaryPhone,
      interestedPrograms: school.interestedPrograms,
      numberOfPaidUsers: school.numberOfPaidUsers,
      numberOfLeads: school.numberOfLeads,
      websiteUrl: school.websiteUrl,
      schoolId: school.schoolId,
      notes: school.notes,
      timing: school.timing
    })
    setIsModalVisible(true)
  }, [form])

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const formData = {
        schoolName: values.schoolName,
        schoolType: values.schoolType,
        place: values.place,
        poc: {
          name: values["poc.name"],
          email: values["poc.email"],
          phone: values["poc.phone"],
          designation: values["poc.designation"],
          secondaryPOC: values["poc.secondaryPOC"],
          secondaryPhone: values["poc.secondaryPhone"]
        },
        interestedPrograms: values.interestedPrograms || [],
        numberOfPaidUsers: values.numberOfPaidUsers || 0,
        numberOfLeads: values.numberOfLeads || 0,
        websiteUrl: values.websiteUrl,
        schoolId: values.schoolId,
        notes: values.notes,
        timing: values.timing
      }

      if (isCreateMode) {
        await axios.post(
          `${constants.NEW_DOMAIN}/api/v1/ic3school`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_JWT_TOKEN}`
            }
          }
        )
        message.success("School created successfully")
      } else {
        await axios.patch(
          `${constants.NEW_DOMAIN}/api/v1/ic3school/${selectedSchool._id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_JWT_TOKEN}`
            }
          }
        )
        message.success("School updated successfully")
      }
      
      setIsModalVisible(false)
      setIsCreateMode(false)
      fetchAllSchools()
    } catch (error) {
      message.error(isCreateMode ? "Failed to create school" : "Failed to update school")
      console.error(error)
    }
  }

  // Initialize data on component mount
  useEffect(() => {
    fetchAllSchools()
    fetchCities()
  }, [])

  // Implement infinite scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 100) {
        if (displayCount < filteredSchools.length) {
          setDisplayCount(prev => Math.min(prev + 12, filteredSchools.length))
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [displayCount, filteredSchools.length])

  // Reset display count when search changes
  useEffect(() => {
    setDisplayCount(12)
  }, [searchTerm])


  return (
    <div className="ic3-dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <Row align="middle" justify="space-between" gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Title level={2} style={{ margin: 0, color: "#262626" }}>
                <ScheduleFilled style={{ marginRight: "12px" }} />
                IC3 Schools Dashboard
              </Title>
              <Text type="secondary">Manage and monitor IC3 partner schools</Text>
            </Col>
            {/* <Col xs={24} sm={12} style={{ textAlign: "right" }}>
              <Space size="large" className="dashboard-stats">
                <div style={{ textAlign: "center" }}>
                  <Title level={3} style={{ margin: 0, color: "#1890ff" }}>{schools.length}</Title>
                  <Text type="secondary">Total Schools</Text>
                </div>
                <div style={{ textAlign: "center" }}>
                  <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
                    {totalUsers}
                  </Title>
                  <Text type="secondary">Total Users</Text>
                </div>
              </Space>
            </Col> */}
          </Row>
          
          <Divider />
          
          <Row gutter={[16, 16]} align="middle" className="search-controls">
            <Col xs={24} md={16}>
              <Input
                size="large"
                placeholder="Search schools..."
                prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
                value={searchTerm}
                onChange={handleSearchChange}
                allowClear
                className="search-input"
                style={{ 
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}
              />
            </Col>
            <Col xs={24} md={8} className="button-group" style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Button
                size="large"
                type="primary"
                icon={<PlusOutlined />}
                onClick={enableCreateMode}
                style={{ borderRadius: "8px", flex: "1 1 auto" }}
              >
                <span>Create</span>
              </Button>
              <Button
                size="large"
                icon={<ReloadOutlined />}
                onClick={fetchAllSchools}
                loading={loading}
                style={{ borderRadius: "8px" }}
                title="Refresh"
              />
            </Col>
          </Row>
        </div>

        {loading ? (
          <Row gutter={[16, 16]}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Col xs={24} sm={12} lg={8} key={item}>
                <Card>
                  <Skeleton active />
                </Card>
              </Col>
            ))}
          </Row>
        ) : filteredSchools.length > 0 ? (
          <>
            <Row gutter={[16, 16]}>
              {filteredSchools.slice(0, displayCount).map((school) => (
                <Col xs={24} sm={12} lg={8} key={school._id}>
                  <SchoolCard 
                    school={school} 
                    onView={showSchoolDetails}
                    onEdit={enableEditMode}
                  />
                </Col>
              ))}
            </Row>
            {displayCount < filteredSchools.length && (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <Button 
                  type="primary" 
                  onClick={() => setDisplayCount(prev => Math.min(prev + 12, filteredSchools.length))}
                >
                  Load More ({filteredSchools.length - displayCount} remaining)
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card style={{ textAlign: "center", padding: "48px", borderRadius: "12px" }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchTerm ? `No schools found matching "${searchTerm}"` : "No schools available"
              }
            />
          </Card>
        )}
      </div>

      <Modal
        title={
          <Space>
            <ScheduleFilled />
            {isCreateMode ? "Create New School" : isEditMode ? "Edit School" : "School Details"}
          </Space>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          setIsCreateMode(false)
        }}
        width={window.innerWidth < 768 ? "100%" : 800}
        footer={
          isCreateMode || isEditMode
            ? [
                <Button key="cancel" onClick={() => {
                  setIsModalVisible(false)
                  setIsCreateMode(false)
                }}>
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  onClick={() => form.submit()}
                >
                  {isCreateMode ? "Create School" : "Save Changes"}
                </Button>
              ]
            : [
                <Button key="close" onClick={() => setIsModalVisible(false)}>
                  Close
                </Button>,
                <Button
                  key="edit"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setIsEditMode(true)}
                >
                  Edit
                </Button>
              ]
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={!isEditMode && !isCreateMode}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="School Name"
                name="schoolName"
                rules={[{ required: true, message: "Please enter school name" }]}
              >
                <Input prefix={<ScheduleFilled />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="School ID" name="schoolId">
                <Input prefix={<IdcardOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="School Type" name="schoolType">
                <Select placeholder="Select school type">
                  <Option value="Boarding">Boarding</Option>
                  <Option value="Day school">Day school</Option>
                  <Option value="Day cum boarding">Day cum boarding</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="City" name="place">
                <Select
                  showSearch
                  placeholder="Search and select city"
                  loading={citiesLoading}
                  onSearch={fetchCities}
                  filterOption={false}
                  notFoundContent={citiesLoading ? <Skeleton.Input style={{ width: 200 }} active size="small" /> : 'No cities found'}
                >
                  {cities.map(city => (
                    <Option key={city} value={city}>{city}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Primary Contact</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="POC Name"
                name="poc.name"
                rules={[{ required: true, message: "Please enter POC name" }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="POC Designation"
                name="poc.designation"
                rules={[{ required: true, message: "Please select POC designation" }]}
              >
                <Select placeholder="Select POC designation">
                  <Option value="Teacher">Teacher</Option>
                  <Option value="Coordinator">Coordinator</Option>
                  <Option value="Counselor">Counselor</Option>
                  <Option value="Principal">Principal</Option>
                  <Option value="Owner">Owner</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="POC Email" name="poc.email">
                <Input type="email" prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="POC Phone"
                name="poc.phone"
                rules={[{ required: true, message: "Please enter POC phone" }]}
              >
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Secondary Contact</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Secondary POC" name="poc.secondaryPOC">
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Secondary Phone" name="poc.secondaryPhone">
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Program Details</Divider>

          <Form.Item label="Interested Programs" name="interestedPrograms">
            <Select
              mode="tags"
              placeholder="Select or add programs"
              style={{ width: "100%" }}
            >  
              <Option value="DUBAI">DUBAI</Option>
              <Option value="ITALY">ITALY</Option>  
              <Option value="JAPAN">JAPAN</Option>
              <Option value="CUSTOM">CUSTOM</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Timing" name="timing">
            <Select
              mode="tags"
              placeholder="Select or add timing"
              style={{ width: "100%" }}
            >
              <Option value="SUMMER">SUMMER</Option>
              <Option value="WINTER">WINTER</Option>  
              <Option value="OTHER">OTHER</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Number of Paid Users" name="numberOfPaidUsers">
                <Input type="number" prefix={<TeamOutlined />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Number of Leads" name="numberOfLeads">
                <Input type="number" prefix={<TrophyOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Website URL" name="websiteUrl">
            <Input prefix={<GlobalOutlined />} />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={4} placeholder="Additional notes or comments..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default IC3SchoolDashboard